import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { PageTitle, StatusBadge, EmptyState } from "@/components/brand";

const OpenMatch = ({ g, onDone }) => {
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/cricket/matches/${g.fixture_id}`).then((r) => setM(r.data)).catch(() => {}); }, [g.fixture_id]);
  const settle = async (winner) => {
    if (!window.confirm(`Settle ${g.match} as ${winner === "void" ? "VOID (refund)" : `${m?.[winner]?.name} won`}?`)) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/cricket/settle", { fixture_id: g.fixture_id, winner }); toast.success(`Settled ${data.settled}${data.failed ? `, ${data.failed} need more admin coins` : ""}`); window.dispatchEvent(new Event("admin-coins-changed")); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`open-match-${g.fixture_id}`} className="card-cr p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><Link to={`/sports/match/${g.fixture_id}`} target="_blank" className="font-semibold hover:text-cr-gold">{g.match}</Link>
          <p className="text-xs text-cr-muted">{g.league} · {dateTime(g.start)}</p></div>
        <p className="text-sm">{g.count} predictions · <b className="text-cr-gold">{g.staked}</b> coins</p>
      </div>
      <p className="mt-3 text-sm text-cr-ink/80">Live status: <b>{m ? m.status : "…"}</b> {m && `· ${m.home.score || "-"} / ${m.away.score || "-"}`} {m?.result && `· ${m.result}`}</p>
      <div className="mt-4 grid gap-2 sm:grid-cols-3">
        {m && ["home", "away"].map((k) => (
          <button key={k} data-testid={`settle-${k}-${g.fixture_id}`} disabled={busy || m.state !== "finished"} onClick={() => settle(k)} className="btn-gold py-2.5 text-xs normal-case">
            {m[k].name} won · {k === "home" ? g.home_stake : g.away_stake} staked</button>))}
        <button data-testid={`settle-void-${g.fixture_id}`} disabled={busy} onClick={() => settle("void")} className="btn-outline py-2.5 text-xs normal-case">Void & refund</button>
      </div>
      {m && m.state !== "finished" && <p className="mt-2 text-xs text-cr-muted">Winner buttons unlock when the match is finished.</p>}
    </div>
  );
};

export default function AdminCricket() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/cricket/predictions").then((r) => setData(r.data)).catch(() => setData({ open: [], recent: [] }));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="admin-cricket-page">
      <PageTitle crumbs={["Admin", "Cricket"]} title="Cricket Predictions" subtitle="Stakes come into your coin balance. Matches settle automatically every 15 minutes once the result is clear; you can also settle or void manually. Winners get 1.9x from your balance." />
      <h2 className="mb-3 text-lg font-bold">Open matches</h2>
      {data?.open.length === 0 && <EmptyState title="No open predictions" text="When your users predict a match it appears here." />}
      <div className="space-y-4">{data?.open.map((g) => <OpenMatch key={g.fixture_id} g={g} onDone={load} />)}</div>
      <h2 className="mb-3 mt-10 text-lg font-bold">Recent predictions</h2>
      <div className="card-cr overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="text-left text-xs uppercase text-cr-muted"><tr>{["User", "Match", "Pick", "Stake", "Payout", "Status"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>{data?.recent.map((p) => (
            <tr key={p.id} className="border-t border-cr-line"><td className="px-4 py-3">{p.user_email}</td><td className="px-4 py-3">{p.match}</td>
              <td className="px-4 py-3">{p.team}</td><td className="px-4 py-3">{p.stake}</td><td className="px-4 py-3 text-cr-gold">{p.payout ?? "-"}</td>
              <td className="px-4 py-3"><StatusBadge status={p.status === "won" ? "completed" : p.status === "lost" ? "rejected" : p.status} /></td></tr>))}</tbody>
        </table>
      </div>
    </div>
  );
}
